import React, { useEffect, useState, useCallback } from "react";
import {
    Shield, ShieldCheck, ShieldAlert, Lock, RefreshCw,
    User, Cpu, Calendar, XCircle, Terminal, Laptop, Layers,
    Key, Search, Copy, Check,
} from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";

interface AssetSecurityProps {
    asset_id?: string | number;
    client_id?: string | number;
    agent_id?: string | number;
    organization_id?: string | number;
    assetId?: string | number;
}

export const AssetSecurity: React.FC<AssetSecurityProps> = ({
    asset_id,
    client_id,
    agent_id,
    organization_id,
    assetId
}) => {
    const [securityData, setSecurityData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [certSearch, setCertSearch] = useState("");
    const [copiedThumbprint, setCopiedThumbprint] = useState<string | null>(null);

    const fetchSecurityStatus = useCallback(async () => {
        setLoading(true);
        try {
            const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
            const finalAssetId = asset_id || assetId || info.asset_id;
            const finalClientId = client_id || info.client_id;
            const finalAgentId = agent_id || info.agent_id;
            const finalOrgId = organization_id || info.organization_id;

            const params = {
                query: JSON.stringify({
                    client_id: finalClientId,
                    organization_id: finalOrgId,
                    agent_id: finalAgentId,
                    asset_id: finalAssetId,
                }),
            };

            const response = await baseapi.get("/asset_security", { params });

            if (response.data?.status) {
                const resData = response.data?.data;
                const item = Array.isArray(resData) ? resData[0] : resData;
                const rawResults = item?.results ?? item;
                let parsed: any = null;

                if (typeof rawResults === "string") {
                    try {
                        parsed = JSON.parse(rawResults);
                    } catch {
                        parsed = null;
                    }
                } else {
                    parsed = rawResults ?? null;
                }

                setSecurityData(parsed);
            } else {
                setSecurityData(null);
            }
        } catch (err: any) {
            showError(err.message || "Failed to fetch security status");
            setSecurityData(null);
        } finally {
            setLoading(false);
        }
    }, [asset_id, client_id, agent_id, organization_id, assetId]);

    useEffect(() => {
        fetchSecurityStatus();
    }, [fetchSecurityStatus]);

    if (loading) {
        return (
            <div className="p-8 text-center text-slate-500 dark:text-gray-400 flex flex-col items-center justify-center space-y-3">
                <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
                <span className="text-xs font-semibold uppercase tracking-wider">Loading Security Status...</span>
            </div>
        );
    }

    if (!securityData) {
        return (
            <div className="p-6 text-center text-slate-600 dark:text-gray-200 flex flex-col items-center justify-center space-y-3 bg-white dark:bg-gray-900/40 rounded-lg border border-slate-200 dark:border-gray-800 m-3 shadow-sm">
                <ShieldAlert className="w-8 h-8 text-amber-500" />
                <p className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-gray-300">No Security Data Available</p>
                <button
                    onClick={fetchSecurityStatus}
                    className="px-3 py-1.5 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-700 dark:text-white rounded text-xs font-semibold border border-slate-200 dark:border-gray-700 transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Retry
                </button>
            </div>
        );
    }

    const platform = securityData.platform?.toLowerCase() || "";
    const isDarwin = platform === "darwin" || platform === "macos";
    const isLinux = platform === "linux";

    const KNOWN_KEYS = [
        "platform", "uac", "os_eol", "auto_update", "secure_boot",
        "local_admins", "disk_encryption", "windows_firewall",
        "antivirusStatus", "firewallStatus", "windowsUpdates", "eventLogSummary",
        "sip", "filevault", "gatekeeper", "mdm", "xprotect", "kernel", "selinux", "apparmor",
        "certificates", "Certificates"
    ];

    const cleanDN = (dn?: string) => {
        if (!dn) return "-";
        const match = dn.match(/CN=([^,]+)/i);
        return match ? match[1].trim() : dn;
    };

    const formatCertDate = (dateStr?: string) => {
        if (!dateStr) return "-";
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
        } catch {
            return dateStr;
        }
    };

    const getCertValidity = (notBefore?: string, notAfter?: string) => {
        const now = Date.now();
        const afterTime = notAfter ? new Date(notAfter).getTime() : null;
        const beforeTime = notBefore ? new Date(notBefore).getTime() : null;

        if (afterTime && afterTime < now) {
            return {
                label: "Expired",
                color: "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20"
            };
        }
        if (beforeTime && beforeTime > now) {
            return {
                label: "Not Yet Active",
                color: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20"
            };
        }
        return {
            label: "Valid",
            color: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
        };
    };

    const handleCopyThumbprint = (thumbprint: string) => {
        navigator.clipboard?.writeText(thumbprint);
        setCopiedThumbprint(thumbprint);
        setTimeout(() => setCopiedThumbprint(null), 2000);
    };

    const renderCertificates = () => {
        const certs: any[] = Array.isArray(securityData?.certificates)
            ? securityData.certificates
            : (Array.isArray(securityData?.Certificates) ? securityData.Certificates : []);

        if (certs.length === 0) return null;

        const filtered = certs.filter((cert) => {
            if (!certSearch.trim()) return true;
            const q = certSearch.toLowerCase();
            return (
                cert.subject?.toLowerCase().includes(q) ||
                cert.issuer?.toLowerCase().includes(q) ||
                cert.thumbprint?.toLowerCase().includes(q) ||
                cert.serialNumber?.toLowerCase().includes(q) ||
                cert.keyAlgorithm?.toLowerCase().includes(q) ||
                cert.signatureAlgorithm?.toLowerCase().includes(q)
            );
        });

        const now = Date.now();
        const expiredCount = certs.filter(c => c.notAfter && new Date(c.notAfter).getTime() < now).length;
        const withPrivKeyCount = certs.filter(c => c.hasPrivateKey).length;

        return (
            <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 mt-4 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 dark:border-gray-700/50 pb-2.5">
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-500/20">
                            <Key className="w-4 h-4" />
                        </div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">
                            Installed Certificates
                        </h3>
                        <div className="flex items-center gap-1.5">
                            <span className="px-2 py-0.5 bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 rounded-full text-[10px] font-bold border border-slate-200 dark:border-gray-700">
                                {certs.length} Total
                            </span>
                            {expiredCount > 0 && (
                                <span className="px-2 py-0.5 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 rounded-full text-[10px] font-bold border border-rose-200 dark:border-rose-500/20">
                                    {expiredCount} Expired
                                </span>
                            )}
                            {withPrivKeyCount > 0 && (
                                <span className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 rounded-full text-[10px] font-bold border border-indigo-200 dark:border-indigo-500/20">
                                    {withPrivKeyCount} PrivKey
                                </span>
                            )}
                        </div>
                    </div>

                    {certs.length > 2 && (
                        <div className="relative min-w-[200px] max-w-xs">
                            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                value={certSearch}
                                onChange={(e) => setCertSearch(e.target.value)}
                                placeholder="Search certificates..."
                                className="w-full pl-8 pr-3 py-1 bg-slate-50 dark:bg-gray-800/80 border border-slate-200 dark:border-gray-700 rounded-lg text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-orange-500"
                            />
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-1">
                    {filtered.length > 0 ? (
                        filtered.map((cert: any, i: number) => {
                            const validity = getCertValidity(cert.notBefore, cert.notAfter);
                            const displayName = cleanDN(cert.subject);

                            return (
                                <div
                                    key={cert.thumbprint || i}
                                    className="bg-slate-50 dark:bg-gray-800/50 rounded-xl border border-slate-200 dark:border-gray-700/50 p-3.5 space-y-2.5 transition-all hover:border-slate-300 dark:hover:border-gray-600 shadow-sm flex flex-col justify-between"
                                >
                                    <div>
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                    <Key className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                                                    <h4 className="text-xs font-bold text-slate-900 dark:text-white truncate" title={cert.subject}>
                                                        {displayName}
                                                    </h4>
                                                </div>
                                                {cert.subject && cert.subject !== displayName && (
                                                    <p className="text-[10px] text-slate-500 dark:text-gray-400 font-mono truncate pl-5" title={cert.subject}>
                                                        {cert.subject}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full border ${validity.color}`}>
                                                    {validity.label}
                                                </span>
                                                {cert.hasPrivateKey && (
                                                    <span className="px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/20 rounded text-[9px] font-bold uppercase tracking-wider" title="Certificate has private key">
                                                        PrivKey
                                                    </span>
                                                )}
                                                {cert.archived && (
                                                    <span className="px-1.5 py-0.5 bg-slate-200 dark:bg-gray-700 text-slate-700 dark:text-gray-300 rounded text-[9px] font-bold uppercase tracking-wider">
                                                        Archived
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-[11px] text-slate-600 dark:text-gray-300 flex items-center gap-1 bg-white/60 dark:bg-gray-900/40 px-2 py-1 rounded border border-slate-200/60 dark:border-gray-700/30">
                                            <span className="text-[10px] uppercase font-bold text-slate-400 dark:text-gray-400 shrink-0">Issuer:</span>
                                            <span className="font-medium text-slate-800 dark:text-gray-200 truncate" title={cert.issuer}>{cert.issuer || "-"}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-slate-200/60 dark:border-gray-700/40">
                                        <div>
                                            <span className="text-[10px] text-slate-500 dark:text-gray-400 block">Key & Algorithm</span>
                                            <span className="text-[11px] font-semibold text-slate-800 dark:text-gray-200">
                                                {cert.keyAlgorithm || "RSA"} {cert.keySize ? `(${cert.keySize}-bit)` : ""}
                                            </span>
                                            {cert.signatureAlgorithm && (
                                                <span className="block text-[10px] font-mono text-slate-500 dark:text-gray-400 truncate" title={cert.signatureAlgorithm}>
                                                    {cert.signatureAlgorithm}
                                                </span>
                                            )}
                                        </div>

                                        <div>
                                            <span className="text-[10px] text-slate-500 dark:text-gray-400 block">Validity Period</span>
                                            <span className="text-[11px] font-medium text-slate-800 dark:text-gray-200 block truncate" title={`${cert.notBefore} → ${cert.notAfter}`}>
                                                {formatCertDate(cert.notBefore)} → {formatCertDate(cert.notAfter)}
                                            </span>
                                            <span className="text-[10px] text-slate-500 dark:text-gray-400 block">
                                                {cert.version ? `v${cert.version}` : ""} {cert.extensionsCount ? `• ${cert.extensionsCount} ext` : ""}
                                            </span>
                                        </div>
                                    </div>

                                    {(cert.thumbprint || cert.serialNumber) && (
                                        <div className="pt-2 border-t border-slate-200/60 dark:border-gray-700/40 space-y-1 text-[10px]">
                                            {cert.thumbprint && (
                                                <div className="flex items-center justify-between gap-1 bg-white/70 dark:bg-gray-900/50 px-2 py-1 rounded border border-slate-200/60 dark:border-gray-700/30">
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <span className="text-slate-400 dark:text-gray-400 font-bold uppercase text-[9px] shrink-0">SHA1:</span>
                                                        <span className="font-mono text-slate-700 dark:text-gray-300 truncate select-all" title={cert.thumbprint}>
                                                            {cert.thumbprint}
                                                        </span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleCopyThumbprint(cert.thumbprint)}
                                                        className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 shrink-0 p-0.5 rounded cursor-pointer transition-colors"
                                                        title="Copy Thumbprint"
                                                    >
                                                        {copiedThumbprint === cert.thumbprint ? (
                                                            <Check className="w-3 h-3 text-emerald-500" />
                                                        ) : (
                                                            <Copy className="w-3 h-3" />
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                            {cert.serialNumber && (
                                                <div className="flex items-center gap-1.5 px-2 py-0.5">
                                                    <span className="text-slate-400 dark:text-gray-400 font-bold uppercase text-[9px] shrink-0">Serial:</span>
                                                    <span className="font-mono text-slate-600 dark:text-gray-400 truncate select-all text-[10px]" title={cert.serialNumber}>
                                                        {cert.serialNumber}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="col-span-full py-8 text-center text-slate-400 dark:text-gray-500 text-xs italic">
                            {certSearch ? `No certificates match "${certSearch}"` : "No certificates found"}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderExtraKeys = () => {
        if (!securityData || typeof securityData !== "object") return null;

        const extraKeys = Object.keys(securityData).filter(key => !KNOWN_KEYS.includes(key));
        if (extraKeys.length === 0) return null;

        return (
            <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 mt-4 shadow-sm">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 border-b border-slate-100 dark:border-gray-700/50 pb-2">
                    <Layers className="w-4 h-4" />
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Additional Security Parameters</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {extraKeys.map((key) => {
                        const value = securityData[key];
                        const formatKeyName = (str: string) => str.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

                        if (typeof value === "boolean") {
                            return (
                                <div key={key} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40">
                                    <span className="text-xs text-slate-700 dark:text-gray-300 font-medium">{formatKeyName(key)}</span>
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${value ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20" : "bg-slate-200/70 dark:bg-gray-700/50 text-slate-600 dark:text-gray-300 border-slate-300 dark:border-gray-600"}`}>
                                        {value ? "Enabled" : "Disabled"}
                                    </span>
                                </div>
                            );
                        }

                        if (typeof value === "string" || typeof value === "number") {
                            return (
                                <div key={key} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40">
                                    <span className="text-xs text-slate-700 dark:text-gray-300 font-medium">{formatKeyName(key)}</span>
                                    <span className="font-mono text-xs text-sky-700 dark:text-sky-300 font-semibold">{String(value)}</span>
                                </div>
                            );
                        }

                        if (Array.isArray(value)) {
                            const isObjectArray = value.length > 0 && typeof value[0] === "object" && value[0] !== null;

                            if (isObjectArray) {
                                return (
                                    <div key={key} className="col-span-full p-3 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div className="text-slate-800 dark:text-gray-200 text-xs font-bold uppercase tracking-wide">
                                                {formatKeyName(key)}
                                            </div>
                                            <span className="px-2 py-0.5 text-[10px] font-bold bg-slate-200 dark:bg-gray-700 text-slate-700 dark:text-gray-300 rounded-full">
                                                {value.length} items
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[360px] overflow-y-auto pr-1">
                                            {value.map((item: any, i: number) => (
                                                <div key={i} className="bg-white dark:bg-gray-900/60 p-2.5 rounded-lg border border-slate-200 dark:border-gray-700/50 space-y-1 text-xs">
                                                    {Object.entries(item).map(([subK, subV]) => (
                                                        <div key={subK} className="flex justify-between items-start gap-2">
                                                            <span className="text-[11px] text-slate-500 dark:text-gray-400 font-medium shrink-0">
                                                                {formatKeyName(subK)}:
                                                            </span>
                                                            <span className="font-mono text-[11px] text-slate-800 dark:text-gray-200 text-right break-all">
                                                                {typeof subV === "boolean" ? (subV ? "True" : "False") : typeof subV === "object" ? JSON.stringify(subV) : String(subV ?? "-")}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={key} className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1">
                                    <div className="text-slate-700 dark:text-gray-200 text-[10px] font-bold uppercase">{formatKeyName(key)}</div>
                                    {value.length > 0 ? (
                                        <div className="flex flex-wrap gap-1">
                                            {value.map((item: any, i: number) => (
                                                <span key={i} className="px-2 py-0.5 bg-slate-200 dark:bg-gray-700 text-slate-800 dark:text-gray-200 rounded text-xs font-mono">
                                                    {String(item)}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-slate-400 dark:text-gray-500 text-xs italic">Empty</span>
                                    )}
                                </div>
                            );
                        }

                        if (typeof value === "object" && value !== null) {
                            return (
                                <div key={key} className="col-span-full md:col-span-1 p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1.5">
                                    <div className="text-slate-700 dark:text-gray-200 text-[10px] font-bold uppercase">{formatKeyName(key)}</div>
                                    <div className="space-y-1 pl-2 border-l border-slate-200 dark:border-gray-700">
                                        {Object.entries(value).map(([subK, subV]) => (
                                            <div key={subK} className="flex justify-between items-center text-xs">
                                                <span className="text-slate-500 dark:text-gray-400">{formatKeyName(subK)}:</span>
                                                <span className="text-slate-800 dark:text-gray-200 font-mono">
                                                    {typeof subV === "boolean"
                                                        ? (subV ? "True" : "False")
                                                        : (typeof subV === "object" ? JSON.stringify(subV) : String(subV))}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        }

                        return null;
                    })}
                </div>
            </div>
        );
    };

    // ----------------------------------------------------
    // Darwin (macOS) Render Component
    // ----------------------------------------------------
    const renderDarwinSecurity = () => {
        const sip = securityData.sip || {};
        const filevault = securityData.filevault || {};
        const gatekeeper = securityData.gatekeeper || {};
        const mdm = securityData.mdm || {};
        const xprotect = securityData.xprotect || {};
        const autoUpdate = securityData.auto_update || {};
        const localAdmins = securityData.local_admins || [];

        return (
            <div className="space-y-4">
                {/* Darwin KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* SIP */}
                    <div className={`p-3.5 rounded-xl border transition-all ${sip.enabled
                        ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400"
                        : "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-400"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">System Integrity (SIP)</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{sip.enabled ? "Enabled" : "Disabled"}</h3>
                            </div>
                            {sip.enabled ? <ShieldCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" /> : <ShieldAlert className="w-6 h-6 text-rose-600 dark:text-rose-400" />}
                        </div>
                        {sip.status && <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2 truncate" title={sip.status}>{sip.status}</p>}
                    </div>

                    {/* FileVault */}
                    <div className={`p-3.5 rounded-xl border transition-all ${filevault.enabled
                        ? "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400"
                        : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">FileVault Encryption</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{filevault.enabled ? "On" : "Off"}</h3>
                            </div>
                            <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        {filevault.status && <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2 truncate" title={filevault.status}>{filevault.status}</p>}
                    </div>

                    {/* Gatekeeper */}
                    <div className={`p-3.5 rounded-xl border transition-all ${gatekeeper.enabled
                        ? "bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/30 text-purple-700 dark:text-purple-400"
                        : "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-400"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">Gatekeeper</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{gatekeeper.enabled ? "Enabled" : "Disabled"}</h3>
                            </div>
                            <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        {gatekeeper.status && <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2 truncate" title={gatekeeper.status}>{gatekeeper.status}</p>}
                    </div>

                    {/* MDM Enrollment */}
                    <div className={`p-3.5 rounded-xl border transition-all ${mdm.enrolled
                        ? "bg-teal-50 dark:bg-teal-500/10 border-teal-200 dark:border-teal-500/30 text-teal-700 dark:text-teal-400"
                        : "bg-slate-50 dark:bg-gray-800/60 border-slate-200 dark:border-gray-700/50 text-slate-600 dark:text-gray-200"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">MDM Enrollment</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{mdm.enrolled ? "Enrolled" : "Not Enrolled"}</h3>
                            </div>
                            <Laptop className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2">DEP: {mdm.dep_enrolled ? "Active" : "Inactive"}</p>
                    </div>
                </div>

                {/* macOS Security Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* XProtect & Software Update */}
                    <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 shadow-sm">
                        <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 border-b border-slate-100 dark:border-gray-700/50 pb-2">
                            <Layers className="w-4 h-4" />
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Malware Defense & Updates</h3>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40">
                                <span className="text-xs text-slate-700 dark:text-gray-300 font-medium">XProtect Version</span>
                                <span className="px-2 py-0.5 bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-500/20 font-mono text-xs font-bold rounded">
                                    {xprotect.version || "Unknown"}
                                </span>
                            </div>

                            <div className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40">
                                <span className="text-xs text-slate-700 dark:text-gray-300 font-medium">Check Updates Automatically</span>
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${autoUpdate.check_enabled
                                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
                                    : "bg-slate-200/70 dark:bg-gray-700/50 text-slate-600 dark:text-gray-200 border-slate-300 dark:border-gray-600"
                                    }`}>
                                    {autoUpdate.check_enabled ? "Yes" : "No"}
                                </span>
                            </div>

                            <div className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40">
                                <span className="text-xs text-slate-700 dark:text-gray-300 font-medium">Install macOS Updates</span>
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${autoUpdate.install_macos_updates
                                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
                                    : "bg-slate-200/70 dark:bg-gray-700/50 text-slate-600 dark:text-gray-200 border-slate-300 dark:border-gray-600"
                                    }`}>
                                    {autoUpdate.install_macos_updates ? "Yes" : "No"}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Local Administrators */}
                    <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 shadow-sm">
                        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 border-b border-slate-100 dark:border-gray-700/50 pb-2">
                            <User className="w-4 h-4" />
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Local Administrators</h3>
                        </div>

                        {localAdmins.length > 0 ? (
                            <div className="flex flex-wrap gap-2 pt-1">
                                {localAdmins.map((admin: string, idx: number) => (
                                    <div key={idx} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-semibold">
                                        <User className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                                        <span>{admin}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-4 text-center text-slate-400 dark:text-gray-500 text-xs italic">
                                No local administrators found
                            </div>
                        )}
                    </div>
                </div>

                {renderCertificates()}
                {renderExtraKeys()}
            </div>
        );
    };

    // ----------------------------------------------------
    // Linux Render Component
    // ----------------------------------------------------
    const renderLinuxSecurity = () => {
        const kernel = securityData.kernel || {};
        const osEol = securityData.os_eol || {};
        const selinux = securityData.selinux || {};
        const apparmor = securityData.apparmor || {};
        const secureBoot = securityData.secure_boot || {};
        const diskEnc = securityData.disk_encryption || {};
        const localAdmins = securityData.local_admins || [];

        return (
            <div className="space-y-4">
                {/* Linux KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* SELinux / AppArmor Status */}
                    <div className={`p-3.5 rounded-xl border transition-all ${selinux.present && selinux.mode?.toLowerCase() === "enforcing"
                        ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400"
                        : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">MAC Security</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                                    {selinux.present ? `SELinux (${selinux.mode || "Active"})` : (apparmor.present ? "AppArmor" : "Disabled")}
                                </h3>
                            </div>
                            <Shield className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2">
                            {selinux.present ? `Mode: ${selinux.mode || "Enforcing"}` : `AppArmor profiles: ${apparmor.profiles_loaded || 0}`}
                        </p>
                    </div>

                    {/* Kernel Status */}
                    <div className={`p-3.5 rounded-xl border transition-all ${kernel.reboot_needed_for_kernel
                        ? "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-400"
                        : "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">Kernel Status</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                                    {kernel.reboot_needed_for_kernel ? "Reboot Needed" : "Up to date"}
                                </h3>
                            </div>
                            <Cpu className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2 truncate" title={kernel.running}>Running: {kernel.running || "-"}</p>
                    </div>

                    {/* Secure Boot */}
                    <div className={`p-3.5 rounded-xl border transition-all ${secureBoot.enabled
                        ? "bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/30 text-purple-700 dark:text-purple-400"
                        : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">Secure Boot</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{secureBoot.enabled ? "Enabled" : "Disabled"}</h3>
                            </div>
                            <Lock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2">EFI System: {secureBoot.efi_system ? "Yes" : "No"}</p>
                    </div>

                    {/* OS EOL */}
                    <div className={`p-3.5 rounded-xl border transition-all ${osEol.is_eol
                        ? "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-400"
                        : "bg-teal-50 dark:bg-teal-500/10 border-teal-200 dark:border-teal-500/30 text-teal-700 dark:text-teal-400"
                        }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">OS EOL Status</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{osEol.is_eol ? "EOL Reached" : "Supported"}</h3>
                            </div>
                            <Calendar className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2">
                            {osEol.days_remaining !== undefined ? `${osEol.days_remaining} days remaining` : `EOL: ${osEol.eol_date || "-"}`}
                        </p>
                    </div>
                </div>

                {/* Detailed Linux Security Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Kernel & OS Lifecycle */}
                    <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 shadow-sm">
                        <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 border-b border-slate-100 dark:border-gray-700/50 pb-2">
                            <Terminal className="w-4 h-4" />
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Kernel & OS Lifecycle</h3>
                        </div>

                        <div className="space-y-2 text-xs">
                            <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1">
                                <div className="text-slate-600 dark:text-gray-200 text-[10px] font-bold uppercase">Running Kernel</div>
                                <div className="font-mono text-emerald-600 dark:text-emerald-400 font-semibold break-all">{kernel.running || "-"}</div>
                            </div>

                            {kernel.latest_installed && (
                                <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1">
                                    <div className="text-slate-600 dark:text-gray-200 text-[10px] font-bold uppercase">Latest Installed Kernel</div>
                                    <div className="font-mono text-sky-600 dark:text-sky-400 font-semibold break-all">{kernel.latest_installed}</div>
                                </div>
                            )}

                            <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 flex items-center justify-between">
                                <span className="text-slate-700 dark:text-gray-300 font-medium">Reboot Required for Kernel</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${kernel.reboot_needed_for_kernel
                                    ? "bg-rose-50 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/30 animate-pulse"
                                    : "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20"
                                    }`}>
                                    {kernel.reboot_needed_for_kernel ? "Reboot Needed" : "No"}
                                </span>
                            </div>

                            <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1">
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-700 dark:text-gray-300 font-medium">Operating System</span>
                                    <span className="text-slate-900 dark:text-white font-bold">{osEol.os || "-"}</span>
                                </div>
                                <div className="flex justify-between items-center text-[11px] pt-1">
                                    <span className="text-slate-500 dark:text-gray-200">EOL Date</span>
                                    <span className="text-teal-600 dark:text-teal-300 font-mono font-semibold">{osEol.eol_date || "-"}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Access Controls & Encryption */}
                    <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 shadow-sm">
                        <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 border-b border-slate-100 dark:border-gray-700/50 pb-2">
                            <Lock className="w-4 h-4" />
                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Security Controls & Encryption</h3>
                        </div>

                        <div className="space-y-2 text-xs">
                            <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 flex items-center justify-between">
                                <div>
                                    <div className="font-semibold text-slate-900 dark:text-white">SELinux</div>
                                    <div className="text-[10px] text-slate-500 dark:text-gray-200">Status: {selinux.present ? "Present" : "Not Installed"}</div>
                                </div>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${selinux.present && selinux.mode?.toLowerCase() === "enforcing"
                                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
                                    : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20"
                                    }`}>
                                    {selinux.mode || (selinux.present ? "Active" : "Disabled")}
                                </span>
                            </div>

                            <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 flex items-center justify-between">
                                <div>
                                    <div className="font-semibold text-slate-900 dark:text-white">AppArmor</div>
                                    <div className="text-[10px] text-slate-500 dark:text-gray-200">Loaded Profiles: {apparmor.profiles_loaded ?? 0}</div>
                                </div>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${apparmor.present
                                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
                                    : "bg-slate-200/70 dark:bg-gray-700/50 text-slate-600 dark:text-gray-200 border-slate-300 dark:border-gray-600"
                                    }`}>
                                    {apparmor.present ? "Present" : "Disabled"}
                                </span>
                            </div>

                            <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 flex items-center justify-between">
                                <div>
                                    <div className="font-semibold text-slate-900 dark:text-white">Disk Encryption</div>
                                    <div className="text-[10px] text-slate-500 dark:text-gray-200">{diskEnc.type || "LUKS / dm-crypt"}</div>
                                </div>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${diskEnc.enabled
                                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
                                    : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20"
                                    }`}>
                                    {diskEnc.enabled ? "Encrypted" : "Disabled"}
                                </span>
                            </div>

                            {/* Local Admins */}
                            <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1">
                                <div className="text-slate-600 dark:text-gray-200 text-[10px] font-bold uppercase mb-1">Local Administrators</div>
                                {localAdmins.length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                        {localAdmins.map((admin: string, idx: number) => (
                                            <span key={idx} className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-300 rounded font-mono text-[11px]">
                                                {admin}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-slate-400 dark:text-gray-500 text-[11px] italic">None configured</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {renderCertificates()}
                {renderExtraKeys()}
            </div>
        );
    };

    // ----------------------------------------------------
    // Windows Security Render Component
    // ----------------------------------------------------
    const renderWindowsSecurity = () => {
        const uac = securityData.uac || {};
        const osEol = securityData.os_eol || {};
        const autoUpdate = securityData.auto_update || {};
        const secureBoot = securityData.secure_boot || {};
        const diskEnc = securityData.disk_encryption || {};
        const firewall = securityData.windows_firewall || {};
        const localAdmins = securityData.local_admins || [];

        const hasNewFormat = securityData.windows_firewall || securityData.disk_encryption || securityData.os_eol || securityData.uac || securityData.secure_boot;
        const hasLegacyFormat = securityData.antivirusStatus || securityData.firewallStatus || securityData.windowsUpdates || securityData.eventLogSummary;

        return (
            <div className="space-y-4">
                {/* Security KPI Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Firewall Card */}
                    <div className={`p-3.5 rounded-xl border transition-all ${
                        firewall.all_profiles_enabled || (securityData.firewallStatus?.some((fw: any) => fw.enabled === 1))
                            ? "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400"
                            : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400"
                    }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">Windows Firewall</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">
                                    {firewall.all_profiles_enabled
                                        ? "All Profiles On"
                                        : (firewall.profiles ? "Partial / Off" : (securityData.firewallStatus ? `${securityData.firewallStatus.filter((fw: any) => fw.enabled === 1).length} Active` : "Disabled"))}
                                </h3>
                            </div>
                            {firewall.all_profiles_enabled ? <ShieldCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" /> : <ShieldAlert className="w-6 h-6 text-amber-600 dark:text-amber-400" />}
                        </div>
                        {firewall.profiles ? (
                            <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2 truncate">
                                Domain: {firewall.profiles.Domain ? "On" : "Off"} | Public: {firewall.profiles.Public ? "On" : "Off"} | Private: {firewall.profiles.Private ? "On" : "Off"}
                            </p>
                        ) : (
                            <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2">Firewall Profile Status</p>
                        )}
                    </div>

                    {/* Disk Encryption (BitLocker) */}
                    <div className={`p-3.5 rounded-xl border transition-all ${
                        diskEnc.enabled
                            ? "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400"
                            : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400"
                    }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">Disk Encryption</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{diskEnc.enabled ? "Encrypted" : "Disabled"}</h3>
                            </div>
                            <Lock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2 truncate">Type: {diskEnc.type || "BitLocker"}</p>
                    </div>

                    {/* Secure Boot */}
                    <div className={`p-3.5 rounded-xl border transition-all ${
                        secureBoot.enabled
                            ? "bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/30 text-purple-700 dark:text-purple-400"
                            : "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400"
                    }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">Secure Boot</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{secureBoot.enabled ? "Enabled" : "Disabled"}</h3>
                            </div>
                            <Shield className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2">EFI System: {secureBoot.efi_system ? "Yes" : "No"}</p>
                    </div>

                    {/* OS EOL */}
                    <div className={`p-3.5 rounded-xl border transition-all ${
                        osEol.is_eol
                            ? "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-400"
                            : "bg-teal-50 dark:bg-teal-500/10 border-teal-200 dark:border-teal-500/30 text-teal-700 dark:text-teal-400"
                    }`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-gray-200">OS EOL Status</p>
                                <h3 className="text-base font-bold text-slate-900 dark:text-white mt-0.5">{osEol.is_eol ? "EOL Reached" : "Supported"}</h3>
                            </div>
                            <Calendar className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                        </div>
                        <p className="text-[10px] text-slate-600 dark:text-gray-300 mt-2">
                            {osEol.days_remaining !== undefined ? `${osEol.days_remaining} days remaining` : `EOL: ${osEol.eol_date || "-"}`}
                        </p>
                    </div>
                </div>

                {/* Detailed Breakdown for New Format */}
                {hasNewFormat && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Firewall & System Controls */}
                        <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 shadow-sm">
                            <div className="flex items-center gap-2 text-sky-600 dark:text-sky-400 border-b border-slate-100 dark:border-gray-700/50 pb-2">
                                <Shield className="w-4 h-4 text-orange-500" />
                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">Firewall & Protection Controls</h3>
                            </div>

                            <div className="space-y-2 text-xs">
                                {firewall.profiles && (
                                    <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-2">
                                        <div className="text-slate-700 dark:text-gray-200 text-[10px] font-bold uppercase">Firewall Profiles</div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {Object.entries(firewall.profiles).map(([profName, isEnabled]) => (
                                                <div key={profName} className="p-2 bg-white dark:bg-gray-900/60 rounded border border-slate-200 dark:border-gray-700/50 text-center shadow-xs">
                                                    <div className="text-[10px] text-slate-500 dark:text-gray-400 font-semibold">{profName}</div>
                                                    <span className={`inline-block mt-1 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded border ${
                                                        isEnabled ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20" : "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20"
                                                    }`}>
                                                        {isEnabled ? "On" : "Off"}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 flex items-center justify-between">
                                    <span className="text-slate-700 dark:text-gray-300 font-medium">User Account Control (UAC)</span>
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${
                                        uac.enabled ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20" : "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/20"
                                    }`}>
                                        {uac.enabled ? "Enabled" : "Disabled"}
                                    </span>
                                </div>

                                <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 flex items-center justify-between">
                                    <span className="text-slate-700 dark:text-gray-300 font-medium">Auto Update Check</span>
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${
                                        autoUpdate.check_enabled ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20" : "bg-slate-200/70 dark:bg-gray-700/50 text-slate-600 dark:text-gray-200 border-slate-300 dark:border-gray-600"
                                    }`}>
                                        {autoUpdate.check_enabled ? "Enabled" : "Disabled"}
                                    </span>
                                </div>

                                <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 flex items-center justify-between">
                                    <span className="text-slate-700 dark:text-gray-300 font-medium">Install Updates Automatically</span>
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded border ${
                                        autoUpdate.install_updates ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20" : "bg-slate-200/70 dark:bg-gray-700/50 text-slate-600 dark:text-gray-200 border-slate-300 dark:border-gray-600"
                                    }`}>
                                        {autoUpdate.install_updates ? "Enabled" : "Disabled"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* OS Lifecycle & User Security */}
                        <div className="bg-white dark:bg-gray-900/50 border border-slate-200 dark:border-gray-700/50 rounded-xl p-4 space-y-3 shadow-sm">
                            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 border-b border-slate-100 dark:border-gray-700/50 pb-2">
                                <Terminal className="w-4 h-4 text-orange-500" />
                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white">OS Lifecycle & Local Access</h3>
                            </div>

                            <div className="space-y-2 text-xs">
                                <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1">
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-700 dark:text-gray-300 font-medium">Operating System</span>
                                        <span className="text-slate-900 dark:text-white font-bold">{osEol.os || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-[11px] pt-1">
                                        <span className="text-slate-500 dark:text-gray-400">OS Version / Build</span>
                                        <span className="text-sky-700 dark:text-sky-300 font-mono font-semibold">{osEol.version || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-[11px] pt-1">
                                        <span className="text-slate-500 dark:text-gray-400">EOL Date</span>
                                        <span className="text-teal-600 dark:text-teal-300 font-mono font-semibold">{osEol.eol_date || "-"}</span>
                                    </div>
                                </div>

                                {/* Local Admins */}
                                <div className="p-2.5 bg-slate-50 dark:bg-gray-800/50 rounded-lg border border-slate-200 dark:border-gray-700/40 space-y-1">
                                    <div className="text-slate-600 dark:text-gray-200 text-[10px] font-bold uppercase mb-1">Local Administrators</div>
                                    {localAdmins.length > 0 ? (
                                        <div className="flex flex-wrap gap-1.5">
                                            {localAdmins.map((admin: string, idx: number) => (
                                                <span key={idx} className="px-2 py-0.5 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-300 rounded font-mono text-[11px]">
                                                    {admin}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-slate-400 dark:text-gray-500 text-[11px] italic">None configured</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Legacy format rendering if present */}
                {hasLegacyFormat && (
                    <div className="space-y-3 pt-2">
                        {securityData.antivirusStatus && (
                            <div className="bg-white dark:bg-gray-900/50 p-4 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm">
                                <h3 className="text-xs font-bold text-emerald-700 dark:text-green-300 mb-3 flex items-center uppercase tracking-wide">
                                    <Shield className="w-4 h-4 mr-2" /> Antivirus Protection
                                </h3>
                                <div className="space-y-2">
                                    {securityStatusHasAV(securityData) ? (
                                        securityData.antivirusStatus.map((av: any, i: number) => (
                                            <div key={i} className="bg-slate-50 dark:bg-gray-800/50 p-3 rounded-lg border border-slate-200 dark:border-gray-700/50 flex items-center justify-between">
                                                <div>
                                                    <p className="text-slate-900 dark:text-white text-xs font-semibold">{av.displayName}</p>
                                                    <p className="text-[10px] text-slate-500 dark:text-gray-400">Product State: {av.productState}</p>
                                                </div>
                                                <span className="px-2 py-0.5 bg-emerald-50 dark:bg-green-500/20 text-emerald-700 dark:text-green-300 border border-emerald-200 dark:border-green-500/30 rounded-full text-[10px] font-bold">
                                                    Active
                                                </span>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="bg-rose-50 dark:bg-red-900/20 p-3 rounded-lg border border-rose-200 dark:border-red-500/30 flex items-center space-x-2">
                                            <XCircle className="w-4 h-4 text-rose-600 dark:text-red-400" />
                                            <span className="text-rose-700 dark:text-red-300 text-xs">No antivirus protection detected</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {renderCertificates()}
                {renderExtraKeys()}
            </div>
        );
    };

    const securityStatusHasAV = (data: any) => Array.isArray(data.antivirusStatus) && data.antivirusStatus.length > 0;

    return (
        <div className="p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 border-b border-slate-200 dark:border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Shield className="w-4 h-4 text-white" />
                    </div>
                    <div>
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Security</h2>
                    </div>
                </div>

                <button
                    onClick={fetchSecurityStatus}
                    disabled={loading}
                    className="p-1.5 rounded bg-white dark:bg-gray-800 text-slate-600 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-gray-700 hover:text-slate-900 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors shadow-sm cursor-pointer"
                    title="Refresh"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                </button>
            </div>

            {/* Platform Specific Rendering */}
            {isDarwin ? renderDarwinSecurity() : isLinux ? renderLinuxSecurity() : renderWindowsSecurity()}
        </div>
    );
};

export default AssetSecurity;
